import { FormattedMenuSectionInfo, ItemModifier, MenuMappingItemDetails, TypeName } from 'api'
import { useIsMobile } from 'hooks'
import React, { FC, Fragment, useEffect, useState } from 'react'
import theme from 'theme'

import SortableModifierContainer from './components/SortableModifierContainer'
import SortableModifierItem from './components/SortableModifierItem'
import * as Styled from './styles'
import { INDEFINITELY_86ED } from './MenuSectionPreview'

type ExpandNestedModifiersMap = {
  [modifierId: ItemModifier['id']]: boolean
}

type Props = {
  subsection: FormattedMenuSectionInfo
  checkedModifierIds?: ItemModifier['id'][]
  isPreviewOnly?: boolean
  onModifierClick?: (modifierId: ItemModifier['id']) => void
  groupModifiers?: FormattedMenuSectionInfo[]
  setSuspendUntilItem?: React.Dispatch<
    React.SetStateAction<
      | {
          id: MenuMappingItemDetails['id'] | ItemModifier['id']
          type: TypeName.Item | TypeName.CustomizationOption
        }
      | undefined
    >
  >
  checkSuspendUntilStatus?: (
    type: TypeName.Item | TypeName.CustomizationOption,
    id: MenuMappingItemDetails['id'] | ItemModifier['id'],
  ) => string
  expandAllModifiers?: boolean
  isItemPreview?: boolean
  isChildItem?: boolean
  isPrinterClient?: boolean
  onModifiersReorder?: (customizationId: string, newModifiers: FormattedMenuSectionInfo[]) => void
  onNestedModifiersReorder?: (parentModifierId: string, nestedCustomizationId: string, newNestedModifiers: FormattedMenuSectionInfo[]) => void
  dragHandle?: React.ReactNode
  isDragging?: boolean
}

const ModifierSubsection: FC<Props> = ({
  subsection,
  isPreviewOnly,
  checkedModifierIds,
  onModifierClick,
  setSuspendUntilItem,
  checkSuspendUntilStatus,
  groupModifiers,
  expandAllModifiers,
  isItemPreview,
  isChildItem,
  isPrinterClient,
  onModifiersReorder,
  onNestedModifiersReorder,
  dragHandle,
  isDragging,
}) => {
  const { isMobile } = useIsMobile()
  const [expandNestedModifiers, setExpandNestedModifiers] = useState<ExpandNestedModifiersMap>({})

  useEffect(() => {
    if (expandAllModifiers) {
      const allModifierWithChildrenIds = groupModifiers
        ?.filter((mod) => mod.children.customizations?.length)
        ?.map((mod) => mod.id)
      const allModifiersMap: ExpandNestedModifiersMap = {}
      allModifierWithChildrenIds?.forEach((id) => {
        allModifiersMap[id] = true
      })

      setExpandNestedModifiers(allModifiersMap)
    } else {
      setExpandNestedModifiers({})
    }
  }, [expandAllModifiers])

  const handleModifiersReorder = (newModifiers: FormattedMenuSectionInfo[]) => {
    if (onModifiersReorder) {
      onModifiersReorder(subsection.id, newModifiers)
    }
  }

  const handleNestedModifiersReorder = (customization: FormattedMenuSectionInfo, newNestedModifiers: FormattedMenuSectionInfo[], parentModifierId: string) => {
    // Update local state immediately for responsive UI
    if (onNestedModifiersReorder) {
      onNestedModifiersReorder(parentModifierId, customization.id, newNestedModifiers)
    }
  }

  // hide the child items when dragging
  if (isDragging) {
    return (
      <Styled.SubsectionContainer style={{ paddingLeft: isChildItem ? (isMobile ? '60px' : '90px') : 0 }}>
        <Styled.HeaderRow>
          {dragHandle}
          <Styled.ModifierTitle>{subsection.attributes.title}</Styled.ModifierTitle>
        </Styled.HeaderRow>
      </Styled.SubsectionContainer>
    )
  }

  return (
    <Styled.SubsectionContainer style={{ paddingLeft: isChildItem ? (isMobile ? '60px' : '90px') : 0 }}>
      <Styled.HeaderRow>
        {dragHandle}
        <Styled.ModifierTitle>{subsection.attributes.title}</Styled.ModifierTitle>
        {isItemPreview && (
          <Styled.TopRowText textAlign='end' fontWeight={theme.fontWeight.normal}>
            Min: {subsection.attributes.min_permitted} / Max: {subsection.attributes.max_permitted}
          </Styled.TopRowText>
        )}
        {isPreviewOnly && (
          <Styled.HeaderText>
            {/* {getRowText(sectionProductsUpdatedCount, sectionModifierUpdatedCount)} */}
          </Styled.HeaderText>
        )}
      </Styled.HeaderRow>

      {groupModifiers && (
        <SortableModifierContainer
          modifiers={groupModifiers}
          onModifiersReorder={handleModifiersReorder}
          isPrinterClient={isPrinterClient}
        >
          {groupModifiers.map((modifier) => {
            return (
              <Fragment key={modifier.id}>
                <SortableModifierItem
                  modifier={modifier}
                  customization={subsection}
                  modifierValues={{
                    title: modifier.attributes.title || '',
                    price: modifier.attributes.price || 0,
                  }}
                  onDeleteModifier={() => {
                    // No delete functionality in preview mode
                  }}
                  isPrinterClient={isPrinterClient}
                  isItemModal={false}
                  isEditor={false}
                  modifiersExist={!!modifier.children.customizations?.length}
                  onRowClick={() =>
                    setExpandNestedModifiers((prevExpandStatus) => ({
                      ...prevExpandStatus,
                      [modifier.id]: !prevExpandStatus[modifier.id],
                    }))
                  }
                  isExpanded={expandNestedModifiers[modifier.id]}
                  isSelected={
                    checkSuspendUntilStatus
                      ? checkSuspendUntilStatus(TypeName.CustomizationOption, modifier.id) === INDEFINITELY_86ED
                      : checkedModifierIds && checkedModifierIds.includes(modifier.id)
                  }
                  onModifierClick={onModifierClick}
                  isPreviewOnly={isPreviewOnly}
                  setSuspendUntilItem={setSuspendUntilItem}
                  existingSuspendUntil={
                    checkSuspendUntilStatus &&
                    checkSuspendUntilStatus(TypeName.CustomizationOption, modifier.id)
                  }
                  checkBoxContainerStyle={{ paddingLeft: 12 }}
                  isItemPreview={isItemPreview}
                />

                {/* Another level of nesting: customization group & modifiers */}
                {(expandNestedModifiers[modifier.id] || isPreviewOnly) &&
                  modifier.children.customizations?.map((customization) => (
                    <Styled.SubsectionContainer key={customization.id}>
                      <Styled.HeaderRow>
                        <Styled.ModifierTitle marginLeft={isMobile ? '30px' : '120px'}>
                          {customization.attributes.title}
                        </Styled.ModifierTitle>
                      </Styled.HeaderRow>
                      <SortableModifierContainer
                        modifiers={customization.children.customization_options || []}
                        onModifiersReorder={(newNestedModifiers) => handleNestedModifiersReorder(customization, newNestedModifiers, modifier.id)}
                        isPrinterClient={isPrinterClient}
                      >
                        {customization.children.customization_options?.map((nestedModifier) => (
                          <SortableModifierItem
                            key={nestedModifier.id}
                            modifier={nestedModifier}
                            customization={customization}
                            modifierValues={{
                              title: nestedModifier.attributes.title || '',
                              price: nestedModifier.attributes.price || 0,
                            }}
                            onDeleteModifier={() => {
                              // No delete functionality in preview mode
                            }}
                            isPrinterClient={isPrinterClient}
                            isItemModal={false}
                            isEditor={false}
                            isSelected={
                              checkSuspendUntilStatus
                                ? checkSuspendUntilStatus(TypeName.CustomizationOption, nestedModifier.id) === INDEFINITELY_86ED
                                : checkedModifierIds && checkedModifierIds.includes(nestedModifier.id)
                            }
                            onModifierClick={onModifierClick}
                            isPreviewOnly={isPreviewOnly}
                            setSuspendUntilItem={setSuspendUntilItem}
                            existingSuspendUntil={
                              checkSuspendUntilStatus &&
                              checkSuspendUntilStatus(TypeName.CustomizationOption, nestedModifier.id)
                            }
                            checkBoxContainerStyle={{ paddingLeft: 12 }}
                            isItemPreview={isItemPreview}
                            isNested={true}
                          />
                        ))}
                      </SortableModifierContainer>
                    </Styled.SubsectionContainer>
                  ))}
              </Fragment>
            )
          })}
        </SortableModifierContainer>
      )}
    </Styled.SubsectionContainer>
  )
}

ModifierSubsection.displayName = 'ModifierSubsection'

export default ModifierSubsection
